/**
 * User Administration Controller
 * Manages the user admin page: listing, creating, editing, and deactivating users.
 *
 * OOA/OOD:
 * - Single Responsibility: user admin page logic only
 * - Template Method: extends BaseController lifecycle
 * - Composition: delegates to canisterClient for backend calls
 * - Encapsulation: all DOM manipulation and state private
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';
import { useProxy } from '../../config/environment';

/**
 * User profile as returned from the canister
 */
interface AdminUserProfile {
    id: string;
    username: string;
    email: string;
    role: { [key: string]: null };
    createdAt: number | bigint;
    lastLogin: number | bigint | null;
    isActive: boolean;
}

/**
 * Candid result wrapper
 */
interface CandidResult<T> {
    ok?: T;
    Ok?: T;
    err?: string;
    Err?: string;
}

/**
 * Role display configuration — maps role keys to labels and badge styles
 */
const ROLE_DISPLAY: Record<string, { label: string; badgeClass: string }> = {
    admin: { label: 'Admin', badgeClass: 'bg-red-100 text-red-800' },
    operator: { label: 'Operator', badgeClass: 'bg-blue-100 text-blue-800' },
    viewer: { label: 'Viewer', badgeClass: 'bg-gray-100 text-gray-800' },
};

/**
 * UserAdminController class
 * Handles all user administration behaviors
 */
export class UserAdminController extends BaseController {
    private users: AdminUserProfile[] = [];

    getPageName(): PageName {
        return 'user-admin';
    }

    protected async doInit(): Promise<void> {
        await this.loadUsers();
    }

    protected setupEventListeners(): void {
        this.setupAddUserButton();
        this.setupAddUserForm();
        this.setupEditUserForm();
        this.setupResetPasswordForm();
        this.setupModalCloseButtons();
        this.setupExportImport();
    }

    // ========================================================================
    // Data Loading — Fetch users from canister
    // ========================================================================

    private async loadUsers(): Promise<void> {
        const loading = document.getElementById('usersTableLoading');
        const table = document.getElementById('usersTable');
        const empty = document.getElementById('usersTableEmpty');

        if (loading) loading.style.display = '';
        if (table) table.style.display = 'none';
        if (empty) empty.style.display = 'none';

        try {
            const token = window.authManager?.getSessionToken();
            if (!token) {
                this.showError('usersTableLoading', 'Not authenticated');
                return;
            }

            const result = await this.canisterClient.callCanister(
                'user_manager', 'getAllUsers', [token]
            ) as CandidResult<AdminUserProfile[]>;

            const data = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (data) {
                this.users = data;
                this.renderUsersTable();
            } else {
                this.showError('usersTableLoading', error || 'Failed to load users');
            }
        } catch (err) {
            console.error('Failed to load users:', err);
            this.showError('usersTableLoading', 'Failed to load users');
        }
    }

    // ========================================================================
    // Table Rendering — Build user table rows from data
    // ========================================================================

    private renderUsersTable(): void {
        const loading = document.getElementById('usersTableLoading');
        const table = document.getElementById('usersTable');
        const empty = document.getElementById('usersTableEmpty');
        const tbody = document.getElementById('usersTableBody');

        if (loading) loading.style.display = 'none';

        if (this.users.length === 0) {
            if (table) table.style.display = 'none';
            if (empty) empty.style.display = '';
            return;
        }

        if (table) table.style.display = '';
        if (empty) empty.style.display = 'none';

        if (!tbody) return;

        tbody.innerHTML = this.users.map(user => this.buildUserRow(user)).join('');

        // Bind row action handlers
        this.bindRowActions();
        this.refreshIcons();
    }

    private buildUserRow(user: AdminUserProfile): string {
        const role = this.extractRole(user);
        const roleConfig = ROLE_DISPLAY[role] || ROLE_DISPLAY['viewer'];
        const statusClass = user.isActive
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800';
        const statusLabel = user.isActive ? 'Active' : 'Inactive';
        const lastLogin = this.formatLastLogin(user.lastLogin);
        const escapedId = this.escapeHtml(user.id);
        const escapedUsername = this.escapeHtml(user.username);

        return `
            <tr data-user-id="${escapedId}">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${escapedUsername}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${this.escapeHtml(user.email)}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                    <span class="px-2 py-1 text-xs rounded-full ${roleConfig.badgeClass}">${roleConfig.label}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                    <span class="px-2 py-1 text-xs rounded-full ${statusClass}">${statusLabel}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${lastLogin}</td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div class="flex items-center justify-end gap-2">
                        <button class="ua-edit-user p-1 text-gray-400 hover:text-green-600" title="Edit User" data-user-id="${escapedId}">
                            <i data-feather="edit-2" class="w-4 h-4"></i>
                        </button>
                        <button class="ua-toggle-status p-1 text-gray-400 hover:text-blue-600" title="${user.isActive ? 'Deactivate' : 'Reactivate'}" data-user-id="${escapedId}" data-active="${user.isActive}">
                            <i data-feather="${user.isActive ? 'user-x' : 'user-check'}" class="w-4 h-4"></i>
                        </button>
                        <button class="ua-reset-password p-1 text-gray-400 hover:text-orange-600" title="Reset Password" data-user-id="${escapedId}" data-username="${escapedUsername}">
                            <i data-feather="key" class="w-4 h-4"></i>
                        </button>
                        <button class="ua-delete-user p-1 text-gray-400 hover:text-red-600" title="Delete User" data-user-id="${escapedId}" data-username="${escapedUsername}">
                            <i data-feather="trash-2" class="w-4 h-4"></i>
                        </button>
                    </div>
                </td>
            </tr>`;
    }

    // ========================================================================
    // User Actions — Create, toggle status, reset password
    // ========================================================================

    private setupAddUserButton(): void {
        const btn = document.getElementById('addUserBtn');
        btn?.addEventListener('click', () => this.showAddUserModal());
    }

    private setupAddUserForm(): void {
        const form = document.getElementById('addUserForm') as HTMLFormElement | null;
        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            await this.handleCreateUser();
        });
    }

    private setupResetPasswordForm(): void {
        const form = document.getElementById('resetPasswordForm') as HTMLFormElement | null;
        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            await this.handleResetPassword();
        });
    }

    private setupEditUserForm(): void {
        const form = document.getElementById('editUserForm') as HTMLFormElement | null;
        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            await this.handleEditUser();
        });
    }

    private setupModalCloseButtons(): void {
        document.querySelectorAll('.add-user-close').forEach(btn => {
            btn.addEventListener('click', () => this.hideAddUserModal());
        });
        document.querySelectorAll('.edit-user-close').forEach(btn => {
            btn.addEventListener('click', () => this.hideEditUserModal());
        });
        document.querySelectorAll('.reset-password-close').forEach(btn => {
            btn.addEventListener('click', () => this.hideResetPasswordModal());
        });
    }

    private bindRowActions(): void {
        document.querySelectorAll('.ua-edit-user').forEach(btn => {
            btn.addEventListener('click', () => {
                const userId = (btn as HTMLElement).dataset.userId!;
                this.showEditUserModal(userId);
            });
        });
        document.querySelectorAll('.ua-toggle-status').forEach(btn => {
            btn.addEventListener('click', async () => {
                const userId = (btn as HTMLElement).dataset.userId!;
                const isActive = (btn as HTMLElement).dataset.active === 'true';
                await this.toggleUserStatus(userId, isActive);
            });
        });
        document.querySelectorAll('.ua-reset-password').forEach(btn => {
            btn.addEventListener('click', () => {
                const userId = (btn as HTMLElement).dataset.userId!;
                const username = (btn as HTMLElement).dataset.username!;
                this.showResetPasswordModal(userId, username);
            });
        });
        document.querySelectorAll('.ua-delete-user').forEach(btn => {
            btn.addEventListener('click', async () => {
                const userId = (btn as HTMLElement).dataset.userId!;
                const username = (btn as HTMLElement).dataset.username!;
                await this.deleteUser(userId, username);
            });
        });
    }

    private async handleCreateUser(): Promise<void> {
        const username = (document.getElementById('au-username') as HTMLInputElement).value.trim();
        const email = (document.getElementById('au-email') as HTMLInputElement).value.trim();
        const password = (document.getElementById('au-password') as HTMLInputElement).value;
        const roleStr = (document.getElementById('au-role') as HTMLSelectElement).value;
        const errorEl = document.getElementById('addUserError');
        const submitBtn = document.getElementById('addUserSubmitBtn') as HTMLButtonElement;

        if (!username || !email || !password) {
            this.showModalError(errorEl, 'All fields are required');
            return;
        }
        if (password.length < 8) {
            this.showModalError(errorEl, 'Password must be at least 8 characters');
            return;
        }

        submitBtn.disabled = true;
        submitBtn.textContent = 'Creating...';

        try {
            const token = window.authManager?.getSessionToken();
            const roleMap: Record<string, any> = {
                admin: { admin: null },
                operator: { operator: null },
                viewer: { viewer: null },
            };

            const result = await this.canisterClient.callCanister(
                'user_manager', 'createUser', [
                    token,
                    { username, email, password, role: roleMap[roleStr] || roleMap['viewer'] }
                ]
            ) as CandidResult<AdminUserProfile>;

            const data = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (data) {
                this.hideAddUserModal();
                await this.loadUsers();
            } else {
                this.showModalError(errorEl, error || 'Failed to create user');
            }
        } catch (err) {
            this.showModalError(errorEl, 'Failed to create user');
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Create User';
        }
    }

    private async toggleUserStatus(userId: string, currentlyActive: boolean): Promise<void> {
        const action = currentlyActive ? 'deactivate' : 'reactivate';
        if (!confirm(`Are you sure you want to ${action} this user?`)) return;

        try {
            const token = window.authManager?.getSessionToken();
            const method = currentlyActive ? 'deactivateUser' : 'reactivateUser';

            const result = await this.canisterClient.callCanister(
                'user_manager', method, [token, userId]
            ) as CandidResult<boolean>;

            const success = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (success) {
                await this.loadUsers();
            } else {
                alert(error || `Failed to ${action} user`);
            }
        } catch (err) {
            alert(`Failed to ${action} user`);
        }
    }

    private async handleEditUser(): Promise<void> {
        const userId = (document.getElementById('eu-user-id') as HTMLInputElement).value;
        const email = (document.getElementById('eu-email') as HTMLInputElement).value.trim();
        const roleStr = (document.getElementById('eu-role') as HTMLSelectElement).value;
        const errorEl = document.getElementById('editUserError');
        const submitBtn = document.getElementById('editUserSubmitBtn') as HTMLButtonElement;

        if (!email) {
            this.showModalError(errorEl, 'Email is required');
            return;
        }

        submitBtn.disabled = true;
        submitBtn.textContent = 'Saving...';

        try {
            const token = window.authManager?.getSessionToken();
            const roleMap: Record<string, any> = {
                admin: { admin: null },
                operator: { operator: null },
                viewer: { viewer: null },
            };

            const result = await this.canisterClient.callCanister(
                'user_manager', 'updateUser', [
                    token, userId,
                    { email, role: roleMap[roleStr] || roleMap['viewer'], isActive: null }
                ]
            ) as CandidResult<AdminUserProfile>;

            const data = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (data) {
                this.hideEditUserModal();
                await this.loadUsers();
            } else {
                this.showModalError(errorEl, error || 'Failed to update user');
            }
        } catch (err) {
            this.showModalError(errorEl, 'Failed to update user');
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Save Changes';
        }
    }

    private async deleteUser(userId: string, username: string): Promise<void> {
        if (!confirm(`Are you sure you want to permanently delete user "${username}"? This cannot be undone.`)) return;

        try {
            const token = window.authManager?.getSessionToken();
            const result = await this.canisterClient.callCanister(
                'user_manager', 'deleteUser', [token, userId]
            ) as CandidResult<boolean>;

            const success = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (success) {
                await this.loadUsers();
            } else {
                alert(error || 'Failed to delete user');
            }
        } catch (err) {
            alert('Failed to delete user');
        }
    }

    private async handleResetPassword(): Promise<void> {
        const userId = (document.getElementById('rp-user-id') as HTMLInputElement).value;
        const newPassword = (document.getElementById('rp-new-password') as HTMLInputElement).value;
        const errorEl = document.getElementById('resetPasswordError');
        const submitBtn = document.getElementById('resetPasswordSubmitBtn') as HTMLButtonElement;

        if (newPassword.length < 8) {
            this.showModalError(errorEl, 'Password must be at least 8 characters');
            return;
        }

        submitBtn.disabled = true;
        submitBtn.textContent = 'Resetting...';

        try {
            const token = window.authManager?.getSessionToken();
            const result = await this.canisterClient.callCanister(
                'user_manager', 'adminResetPassword', [token, userId, newPassword]
            ) as CandidResult<boolean>;

            const success = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (success) {
                this.hideResetPasswordModal();
            } else {
                this.showModalError(errorEl, error || 'Failed to reset password');
            }
        } catch (err) {
            this.showModalError(errorEl, 'Failed to reset password');
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Reset Password';
        }
    }

    // ========================================================================
    // Modal Management — Show/hide modal dialogs
    // ========================================================================

    private showAddUserModal(): void {
        const modal = document.getElementById('addUserModal');
        const form = document.getElementById('addUserForm') as HTMLFormElement | null;
        const errorEl = document.getElementById('addUserError');
        form?.reset();
        if (errorEl) errorEl.style.display = 'none';
        if (modal) modal.style.display = 'flex';
        this.refreshIcons();
    }

    private hideAddUserModal(): void {
        const modal = document.getElementById('addUserModal');
        if (modal) modal.style.display = 'none';
    }

    private showEditUserModal(userId: string): void {
        const user = this.users.find(u => u.id === userId);
        if (!user) return;

        const modal = document.getElementById('editUserModal');
        const form = document.getElementById('editUserForm') as HTMLFormElement | null;
        const errorEl = document.getElementById('editUserError');
        const userIdInput = document.getElementById('eu-user-id') as HTMLInputElement | null;
        const usernameInput = document.getElementById('eu-username') as HTMLInputElement | null;
        const emailInput = document.getElementById('eu-email') as HTMLInputElement | null;
        const roleSelect = document.getElementById('eu-role') as HTMLSelectElement | null;

        form?.reset();
        if (errorEl) errorEl.style.display = 'none';
        if (userIdInput) userIdInput.value = user.id;
        if (usernameInput) usernameInput.value = user.username;
        if (emailInput) emailInput.value = user.email;
        if (roleSelect) roleSelect.value = this.extractRole(user);
        if (modal) modal.style.display = 'flex';
        this.refreshIcons();
    }

    private hideEditUserModal(): void {
        const modal = document.getElementById('editUserModal');
        if (modal) modal.style.display = 'none';
    }

    private showResetPasswordModal(userId: string, username: string): void {
        const modal = document.getElementById('resetPasswordModal');
        const form = document.getElementById('resetPasswordForm') as HTMLFormElement | null;
        const target = document.getElementById('resetPasswordTarget');
        const userIdInput = document.getElementById('rp-user-id') as HTMLInputElement | null;
        const errorEl = document.getElementById('resetPasswordError');

        form?.reset();
        if (errorEl) errorEl.style.display = 'none';
        if (target) target.textContent = `Set new password for "${username}"`;
        if (userIdInput) userIdInput.value = userId;
        if (modal) modal.style.display = 'flex';
        this.refreshIcons();
    }

    private hideResetPasswordModal(): void {
        const modal = document.getElementById('resetPasswordModal');
        if (modal) modal.style.display = 'none';
    }

    // ========================================================================
    // Helpers — Formatting and display utilities
    // ========================================================================

    private extractRole(user: AdminUserProfile): string {
        return Object.keys(user.role)[0] || 'viewer';
    }

    private formatLastLogin(lastLogin: number | bigint | null): string {
        if (!lastLogin) return 'Never';
        const timestamp = Number(lastLogin);
        // IC timestamps are in nanoseconds
        const date = new Date(timestamp / 1_000_000);
        if (isNaN(date.getTime())) return 'Never';
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    private showModalError(el: HTMLElement | null, message: string): void {
        if (el) {
            el.textContent = message;
            el.style.display = 'block';
        }
    }

    // ========================================================================
    // Export / Import — Backup and restore user data as JSON
    // ========================================================================

    private setupExportImport(): void {
        const exportBtn = document.getElementById('exportUsersBtn');
        exportBtn?.addEventListener('click', () => this.handleExport());

        const importFile = document.getElementById('importUsersFile') as HTMLInputElement | null;
        importFile?.addEventListener('change', (e) => this.handleImport(e));
    }

    private async handleExport(): Promise<void> {
        try {
            const token = window.authManager?.getSessionToken();
            if (!token) {
                alert('Not authenticated');
                return;
            }

            const result = await this.canisterClient.callCanister(
                'user_manager', 'exportUsers', [token]
            ) as CandidResult<any[]>;

            const data = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (!data) {
                alert(error || 'Failed to export users');
                return;
            }

            // Normalize Candid optional fields for clean JSON export:
            // AgentAdapter returns [] for None, [value] for Some — convert to null/bare value
            const normalizedData = data.map((user: any) => ({
                ...user,
                salt: Array.isArray(user.salt) ? (user.salt.length > 0 ? user.salt[0] : null) : user.salt,
                lastLogin: Array.isArray(user.lastLogin) ? (user.lastLogin.length > 0 ? user.lastLogin[0] : null) : user.lastLogin,
            }));

            // BigInt replacer for JSON serialization (timestamps from AgentAdapter)
            const blob = new Blob([JSON.stringify(normalizedData, (_key, value) =>
                typeof value === 'bigint' ? Number(value) : value
            , 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `asd-users-${new Date().toISOString().slice(0, 10)}.json`;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);
            alert('Failed to export users');
        }
    }

    private async handleImport(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            const importData = JSON.parse(text);

            if (!Array.isArray(importData)) {
                alert('Invalid file format: expected a JSON array of user records');
                input.value = '';
                return;
            }

            if (!confirm(`Import ${importData.length} user(s)? Existing usernames will be skipped.`)) {
                input.value = '';
                return;
            }

            const token = window.authManager?.getSessionToken();
            if (!token) {
                alert('Not authenticated');
                input.value = '';
                return;
            }

            // Normalize optional fields for Candid encoding:
            // AgentAdapter (mainnet) needs [] for None, [value] for Some
            // ProxyAdapter (asd-dev/local) needs null/bare values
            const normalizedData = useProxy()
                ? importData
                : importData.map((user: any) => ({
                    ...user,
                    salt: user.salt != null ? [user.salt] : [],
                    lastLogin: user.lastLogin != null ? [user.lastLogin] : [],
                }));

            const result = await this.canisterClient.callCanister(
                'user_manager', 'importUsers', [token, normalizedData]
            ) as CandidResult<{ imported: number; skipped: number }>;

            const data = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (data) {
                alert(`Import complete: ${data.imported} imported, ${data.skipped} skipped (duplicate usernames)`);
                await this.loadUsers();
            } else {
                alert(error || 'Failed to import users');
            }
        } catch (err) {
            console.error('Import failed:', err);
            alert('Failed to import users. Check that the file is valid JSON.');
        } finally {
            input.value = '';
        }
    }
}
